/**
 * Platform detection for PrepMaster.
 *
 * Works today (web only) and once Capacitor is added it will automatically
 * detect the native iOS / Android runtimes without any code changes.
 *
 * We branch specifically on iOS because Apple's App Store Review
 * Guideline 3.1.3(a) forbids showing in-app purchase-like flows for digital
 * goods; on iOS we must send students to prepmaster.ph to complete
 * enrollment. Android (Google Play) is far more permissive and lets us keep
 * the full in-app payment flow.
 */

// The web build ships without Capacitor and simply behaves like today.
// Once we wrap the app with Capacitor, `window.Capacitor` becomes available
// and returns "ios" / "android" via `getPlatform()`.
function _detectPlatform() {
  if (typeof window === "undefined") return "web";
  try {
    // Capacitor v3+ exposes a global for JS bridge detection.
    const c = window.Capacitor;
    if (c && typeof c.getPlatform === "function") {
      return c.getPlatform(); // "ios" | "android" | "web"
    }
    if (c && typeof c.platform === "string") return c.platform;
  } catch { /* noop */ }
  return "web";
}

function platform() {
  // No caching — detection is cheap and needs to react to Capacitor being
  // injected at any point (helpful for hot-reload / testing with a mocked
  // Capacitor global).
  return _detectPlatform();
}

export function isNativeIOS() {
  return platform() === "ios";
}

export function isNativeAndroid() {
  return platform() === "android";
}

export function isNativeApp() {
  const p = platform();
  return p === "ios" || p === "android";
}

export function isWeb() {
  return platform() === "web";
}

// URL of the marketing / paid web app. Consumed by <ContinueOnWebCard/>
// and the deep-link redirect helper below. Falls back to the current origin
// so local dev + preview still resolve correctly.
export function webAppUrl() {
  try {
    if (isWeb() && typeof window !== "undefined") return window.location.origin;
  } catch { /* noop */ }
  // Production canonical URL for redirects from the native iOS app.
  return "https://prepmaster.ph";
}

/**
 * Open the given path on the public web (opens Safari when inside the
 * native iOS app; opens a normal tab on web).
 */
export async function openOnWeb(pathOrUrl) {
  const target = pathOrUrl.startsWith("http")
    ? pathOrUrl
    : `${webAppUrl().replace(/\/$/, "")}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
  try {
    // If Capacitor Browser plugin is installed we prefer that for a proper
    // SFSafariViewController on iOS (looks native + no session confusion).
    const c = typeof window !== "undefined" ? window.Capacitor : null;
    if (c && c.Plugins && c.Plugins.Browser && typeof c.Plugins.Browser.open === "function") {
      await c.Plugins.Browser.open({ url: target });
      return;
    }
  } catch { /* noop */ }
  // Web / no Capacitor: just open in a new tab.
  if (typeof window !== "undefined") window.open(target, "_blank", "noopener,noreferrer");
}
